import type { Survivor, WorkstationId, WorkstationRuntimeState } from './types'

const LODGING_REGEN_STEP_MS = 60_000

/** Max survivors without lodging contribution (lodging adds bonuses per upgrade level). */
export const BASE_MAX_SURVIVORS = 5

/** Bonus slots unlocked at lodging workstation levels 1, 2, 3 (cumulative). */
export const LODGING_SURVIVOR_BONUSES = [3, 5, 10] as const

/** Max survivors resting in lodging at each workstation level (built, level ≥ 1). */
export const LODGING_REST_CAPACITY_BY_LEVEL: Record<number, number> = {
  1: 2,
  2: 4,
  3: 6,
}

/** Energy points restored per minute while resting in lodging (idle, not injured). */
export const LODGING_ENERGY_REGEN_PER_MINUTE_BY_LEVEL: Record<number, number> = {
  1: 4,
  2: 6,
  3: 10,
}

export function lodgingSurvivorBonusTotal(level: number): number {
  const lv = Math.max(0, Math.floor(level))
  let sum = 0
  for (let i = 0; i < Math.min(lv, LODGING_SURVIVOR_BONUSES.length); i++) {
    sum += LODGING_SURVIVOR_BONUSES[i]!
  }
  return sum
}

export function getMaxSurvivors(
  workstations: Partial<Record<WorkstationId, WorkstationRuntimeState>>,
): number {
  const lodging = workstations.lodging
  if (!lodging?.built || lodging.level < 1) return BASE_MAX_SURVIVORS
  return BASE_MAX_SURVIVORS + lodgingSurvivorBonusTotal(lodging.level)
}

export function getLodgingRestCapacity(workstations: WorkstationRuntimeState | undefined): number {
  if (!workstations?.built || workstations.level < 1) return 0
  return LODGING_REST_CAPACITY_BY_LEVEL[workstations.level] ?? 6
}

export function getLodgingEnergyRegenPerMinute(
  workstations: WorkstationRuntimeState | undefined,
): number {
  if (!workstations?.built || workstations.level < 1) return 0
  return LODGING_ENERGY_REGEN_PER_MINUTE_BY_LEVEL[workstations.level] ?? 10
}

/** Passive energy recovery for survivors resting at lodging (Idle only). */
export function resolveLodgingEnergyRegen(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  survivors: Survivor[],
  now: number,
): {
  survivors: Survivor[]
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  changed: boolean
} {
  const ws = workstations.lodging
  if (!ws?.built || ws.level < 1) {
    return { survivors, workstations, changed: false }
  }

  const clampNeed = (n: number) => Math.max(0, Math.min(100, n))

  const idleOccupants = ws.lodgingOccupantIds.filter((id) => {
    const s = survivors.find((x) => x.id === id)
    return s?.status === 'Idle'
  })
  let lodgingWs = ws
  let occupantListDirty =
    idleOccupants.length !== ws.lodgingOccupantIds.length

  if (occupantListDirty) {
    lodgingWs = { ...ws, lodgingOccupantIds: idleOccupants }
  }

  if (idleOccupants.length === 0) {
    if (!occupantListDirty) return { survivors, workstations, changed: false }
    return {
      survivors,
      workstations: { ...workstations, lodging: lodgingWs },
      changed: true,
    }
  }

  const anchor = lodgingWs.needRegenAnchorAt ?? now
  if (anchor > now) {
    return {
      survivors,
      workstations: occupantListDirty ? { ...workstations, lodging: lodgingWs } : workstations,
      changed: occupantListDirty,
    }
  }

  const elapsedMs = now - anchor
  const elapsedMinutes = Math.floor(elapsedMs / LODGING_REGEN_STEP_MS)
  if (elapsedMinutes <= 0) {
    if (lodgingWs.needRegenAnchorAt == null) {
      return {
        survivors,
        workstations: {
          ...workstations,
          lodging: { ...lodgingWs, needRegenAnchorAt: now },
        },
        changed: true,
      }
    }
    if (!occupantListDirty) return { survivors, workstations, changed: false }
    return {
      survivors,
      workstations: { ...workstations, lodging: lodgingWs },
      changed: true,
    }
  }

  const rate = getLodgingEnergyRegenPerMinute(lodgingWs)
  const gain = elapsedMinutes * rate
  const occupantSet = new Set(idleOccupants)

  const nextSurvivors = survivors.map((s) => {
    if (!occupantSet.has(s.id) || s.status !== 'Idle') return s
    const nextEnergy = clampNeed(s.needs.energy + gain)
    if (nextEnergy === s.needs.energy) return s
    return { ...s, needs: { ...s.needs, energy: nextEnergy } }
  })

  const nextLodging: WorkstationRuntimeState = {
    ...lodgingWs,
    needRegenAnchorAt: anchor + elapsedMinutes * LODGING_REGEN_STEP_MS,
  }

  return {
    survivors: nextSurvivors,
    workstations: { ...workstations, lodging: nextLodging },
    changed: true,
  }
}
